import { useCallback, useMemo, useRef, useState } from 'react'
import { FiPlus } from 'react-icons/fi'
import { useParams } from 'react-router-dom'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search } from '@vtex/shoreline'
import { t } from 'i18next'
import debounce from 'lodash/debounce'

import { Button } from '~/components/Buttons'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import Tooltip from '~/components/Tooltip'
import { isAdvertiser } from '~/helpers'
import { useSidePanel } from '~/hooks/useSidePanel'

import {
  listCampaignSubpublishersKey,
  listCampaignSubpublishersService
} from '../../services/campaign/subpublisher'
import {
  listSubpublisherApprovalStatusKey,
  listSubpublisherApprovalStatusService
} from '../../services/campaign/subpublisher-approval-status'
import {
  getApprovalStatusRefetchInterval,
  isAwaitingSubpublisherDistribution,
  shouldPollApprovalStatusForDistribution,
  shouldPollCuratedListForDistribution
} from '../../services/campaign/subpublisher-approval-status/poll'
import {
  listSubpublishersKey,
  listSubpublishersService
} from '../../services/publisher/subpublishers'
import CampaignAddSubpublishersForm from './AddForm'
import { buildSchema } from './schema'

const HandleSubplishers = ({
  publisherId,
  campaignData
}: {
  publisherId: PublisherId
  campaignData?: CampaignView
}) => {
  /**
   * Get subplishers from publisher
   */
  const { id } = useParams() as { id: CampaignId }
  const queryClient = useQueryClient()

  const requireApproval =
    !!campaignData?.settings?.require_subpublisher_approval
  const awaitingDistribution =
    requireApproval && isAwaitingSubpublisherDistribution(campaignData?.status)
  const approvalStatusQueryKey = [listSubpublisherApprovalStatusKey, id]
  const allSubpublishersQueryKey = [listCampaignSubpublishersKey, id, 'all']

  const getCachedApprovalCount = () =>
    queryClient.getQueryData<{ publishers?: { publisher_id: string }[] }>(
      approvalStatusQueryKey
    )?.publishers?.length ?? 0

  const getCachedCuratedRows = () =>
    queryClient.getQueryData<{ data?: ListCampaignSubpublisherResponse[] }>(
      allSubpublishersQueryKey
    )?.data ?? []

  const sectionRef = useRef<HTMLDivElement>(null)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [searchValue, setSearchValue] = useState('')
  const [name, setName] = useState('')

  const queryKey = [listCampaignSubpublishersKey, id, page, perPage, name]

  const { data, isPending } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await listCampaignSubpublishersService({
        id,
        params: {
          page,
          quantity: perPage,
          count: true,
          name: name || undefined
        }
      })

      return response.data
    },
    placeholderData: previousData => previousData,
    refetchInterval: query =>
      getApprovalStatusRefetchInterval(
        shouldPollCuratedListForDistribution({
          awaitingDistribution: awaitingDistribution && !name,
          curatedCount: query.state.data?.data.length ?? 0,
          approvalCount: getCachedApprovalCount()
        })
      )
  })

  const handlePagination = useCallback((newPage: number) => {
    setPage(newPage)
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handleItemsPerPageChange = useCallback(
    ({ value }: { value: number }) => {
      setPerPage(value)
      setPage(1)
    },
    []
  )

  const handleDebounceSearch = useRef(
    debounce((value: string) => {
      setName(value)
      setPage(1)
    }, 500)
  ).current

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value)
      handleDebounceSearch(e.target.value)
    },
    [handleDebounceSearch]
  )

  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    setName('')
    setPage(1)
  }, [])

  /**
   * Full list of subpublishers already linked to the campaign, regardless of
   * the table's current page — used to check for active subpublishers and to
   * exclude already-linked subpublishers from the "add" panel
   */
  const { data: allSubpublishersData } = useQuery({
    queryKey: allSubpublishersQueryKey,
    queryFn: async () => {
      const response = await listCampaignSubpublishersService({
        id,
        params: { quantity: 1000, count: true }
      })

      return response.data
    },
    refetchInterval: query =>
      getApprovalStatusRefetchInterval(
        shouldPollCuratedListForDistribution({
          awaitingDistribution: awaitingDistribution && !name,
          curatedCount: query.state.data?.data.length ?? 0,
          approvalCount: getCachedApprovalCount()
        })
      )
  })

  const hasActivePublisher = useMemo(
    () => allSubpublishersData?.data.some(subpublisher => subpublisher.active),
    [allSubpublishersData]
  )

  const keyMessages = useMemo(
    () => (hasActivePublisher ? 'selected' : 'fullNetwork'),
    [hasActivePublisher]
  )

  /**
   * Handle available subpublishers
   */
  const selectedPublishersIds = useMemo(
    () => allSubpublishersData?.data?.map(item => item.subpublisher_id) ?? [],
    [allSubpublishersData?.data]
  )

  const queryKeySubpublishers = [
    listSubpublishersKey,
    publisherId,
    requireApproval ? id : null
  ]

  /**
   * Roster da rede: não vai para a tabela (que lista só a lista curada), serve
   * apenas para saber se ainda há elegíveis a adicionar (habilita o botão
   * "Adicionar") e quantos inelegíveis o painel lateral deve avisar.
   */
  const { data: subpublishersData } = useQuery({
    queryKey: queryKeySubpublishers,
    queryFn: async () => {
      const response = await listSubpublishersService({
        params: {
          publisher_id: publisherId,
          // Mesmo teto da query da lista curada, para a contagem de
          // elegíveis/inelegíveis não truncar.
          quantity: 1000,
          // Com `campaign_id` a API devolve a rede toda com o flag `eligible`
          // (tem status de revisão OU formato de anúncio compatível).
          ...(requireApproval ? { campaign_id: id } : {})
        }
      })

      return response.data
    }
  })

  // Fora do modo aprovação a API não manda o flag: a rede inteira é elegível.
  const eligibleSubpublishers = useMemo(
    () => subpublishersData?.data.filter(item => item.eligible !== false) ?? [],
    [subpublishersData?.data]
  )

  const ineligibleCount =
    (subpublishersData?.data.length ?? 0) - eligibleSubpublishers.length

  const notAddedSubpublishers = useMemo(
    () =>
      eligibleSubpublishers.filter(
        item => !selectedPublishersIds?.includes(item.id)
      ),
    [selectedPublishersIds, eligibleSubpublishers]
  )

  const hasAvaliableSubpublishers = notAddedSubpublishers.length > 0

  /**
   * Approval status — only fetched once per campaign when the campaign
   * requires sub-publisher approval.
   */
  const { data: approvalStatusData } = useQuery({
    queryKey: approvalStatusQueryKey,
    queryFn: async () => {
      const response = await listSubpublisherApprovalStatusService({ id })

      return response.data
    },
    enabled: requireApproval,
    retry: false,
    refetchInterval: query =>
      getApprovalStatusRefetchInterval(
        shouldPollApprovalStatusForDistribution({
          awaitingDistribution,
          curatedRows: getCachedCuratedRows(),
          approvalPublishers: query.state.data?.publishers
        })
      )
  })

  const approvalByPublisherId = useMemo(() => {
    const map = new Map<string, SubpublisherApprovalStatus>()

    approvalStatusData?.publishers?.forEach(item => {
      map.set(item.publisher_id, item)
    })

    return map
  }, [approvalStatusData])

  const curatedRows = useMemo(
    () =>
      (data?.data ?? []).map(row => ({
        ...row,
        approval: approvalByPublisherId.get(row.subpublisher_id)
      })),
    [approvalByPublisherId, data?.data]
  )

  // Handle open panel
  const { addSidePanel, removeSidePanel } = useSidePanel()

  const handleOpenPanel = useCallback(() => {
    addSidePanel({
      title: t('rm:campaign.subpublishers.add.panel.title'),
      type: 'content',
      size: 'large',
      content: (
        <CampaignAddSubpublishersForm
          publisherId={publisherId}
          selectedPublishers={selectedPublishersIds}
          onClose={removeSidePanel}
          campaignId={id}
          hasAvaliableSubpublishers={hasAvaliableSubpublishers}
          requireApproval={requireApproval}
          ineligibleCount={ineligibleCount}
        />
      )
    })
  }, [
    addSidePanel,
    hasAvaliableSubpublishers,
    id,
    ineligibleCount,
    publisherId,
    removeSidePanel,
    requireApproval,
    selectedPublishersIds
  ])

  const tooltipAllSelectedTitle = t(
    'rm:campaign.subpublishers.add.tooltip.allSelected'
  )
  const tooltipAllSelectedText = (
    <>
      {t('rm:campaign.subpublishers.add.alert.alreadySelectAll')}
      {requireApproval && ineligibleCount > 0 ? (
        <p className="mb-0 mt-2">
          {t('rm:campaign.subpublishers.add.ineligibleNotice', {
            count: ineligibleCount
          })}
        </p>
      ) : null}
    </>
  )

  const addButton = (
    <Button
      text={t('c:actions.add')}
      size="xsmall"
      customWidth="auto"
      iconLeft={<FiPlus />}
      onClick={handleOpenPanel}
      disabled={!hasAvaliableSubpublishers}
    />
  )

  return (
    <div ref={sectionRef}>
      <Panel
        isCollapsible
        unmountContentIfCollapse
        noPaddingBody
        title={t('rm:campaign.subpublishers.manage.title')}
        description={t(`rm:campaign.subpublishers.${keyMessages}.title`)}
      >
        <header className="d-flex gap-3 justify-content-between px-3 py-2">
          <section className="d-flex flex-column">
            <span className="text-sm text-gray-500 lh-1">
              {t(`rm:campaign.subpublishers.${keyMessages}.description`)}
            </span>
          </section>
          {isAdvertiser() && (
            <aside className="ml-auto">
              {hasAvaliableSubpublishers ? (
                addButton
              ) : (
                <Tooltip
                  noMargin
                  interactiveTrigger
                  title={tooltipAllSelectedTitle}
                  text={tooltipAllSelectedText}
                  icon={addButton}
                />
              )}
            </aside>
          )}
        </header>

        <div className="px-3 pb-2 pt-2">
          <Search
            value={searchValue}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            messages={{
              placeholder: t('rm:campaign.subpublishers.search.placeholder')
            }}
          />
        </div>

        <Loading status={isPending} onlyLine />

        {!!data && (
          <>
            <Table
              data={curatedRows}
              schema={buildSchema({
                withToggle: true,
                withApproval: requireApproval,
                awaitingDistribution
              })}
              total={data.total}
              smallerRowPadding
              hasHeader={false}
              emptyStateText={
                name ? t('rm:campaign.subpublishers.search.notFound') : ''
              }
            />

            <div className="px-3 py-2">
              <ListPagination
                total={data.total}
                label={data.total > 1 ? 'publishers' : 'publisher'}
                currentPage={page}
                itemsPerPage={perPage}
                onClickPagination={handlePagination}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </>
        )}
      </Panel>
    </div>
  )
}

export default HandleSubplishers
